import { FEATURES } from '../../app/terminology/features';
import {
    Banner,
    BlockStack,
    Button,
    Card,
    DatePicker,
    Divider,
    InlineStack,
    Page,
    Pagination,
    Popover,
    Select,
    Spinner,
    Text,
    TextField,
} from '@shopify/polaris';
import { ArrowLeftIcon, CalendarIcon, ChevronDownIcon, ChevronUpIcon, ExportIcon, InfoIcon } from '@shopify/polaris-icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import agent from '../../app/api/agent';
import { getApiErrorMessage } from '../../app/api/error';
import {
    CoverageFilter,
    FitmentCoverageHealthReport,
    FitmentCoverageHealthVehicle,
    StorefrontSearchReport,
    StorefrontSearchReportTopSearch,
} from '../../app/models/Reports';
import {
    formatSearchDateLabel,
    SearchActivityChart,
} from './SearchActivitySummary';
import './ReportsPage.css';
import VehicleReportDetails, { getSearchLabel } from './VehicleReportDetails';
import { csvSharePercent, formatSharePercent, getSharePercent } from './reportShare';

type ReportRange = '7' | '30' | '90' | 'custom';
type ComparisonPeriod =
    | 'none'
    | 'previous_period'
    | 'previous_year'
    | 'previous_year_weekday'
    | 'custom';
type ComparisonDateRange = {
    start: Date;
    end: Date;
};
type DailyBreakdownRow = {
    currentDate: string;
    currentSearches: number;
    currentDataAvailable: boolean;
    comparisonDate?: string;
    comparisonSearches?: number;
    comparisonDataAvailable?: boolean;
};
type ReportKind =
    | 'daily_search_volume'
    | 'top_vehicle_searches'
    | 'coverage_opportunities'
    | 'fitment_coverage_health';
type VehicleSort = 'searches' | 'increase' | 'decrease';

type ReportDefinition = {
    value: ReportKind;
    label: string;
};

const reportDefinitions: ReportDefinition[] = [
    {
        value: 'daily_search_volume',
        label: 'Daily search volume',
    },
    {
        value: 'top_vehicle_searches',
        label: 'Top vehicle searches',
    },
    {
        value: 'coverage_opportunities',
        label: 'Coverage opportunities',
    },
    {
        value: 'fitment_coverage_health',
        label: 'Fitment coverage health',
    },
];

const reportOptions = reportDefinitions.map((definition) => ({
    label: definition.label,
    value: definition.value,
}));

const getInitialReport = (): ReportKind => {
    const requestedReport = new URLSearchParams(window.location.search).get('report');
    if (requestedReport === 'search_demand_trends') return 'top_vehicle_searches';

    // Preserve older dashboard/deep links that pointed to the combined
    // Search activity report before its two views became standalone reports.
    if (requestedReport === 'search_activity') {
        return 'daily_search_volume';
    }

    return reportDefinitions.some((definition) => definition.value === requestedReport)
        ? requestedReport as ReportKind
        : 'daily_search_volume';
};

const rangeOptions = [
    { label: 'Last 7 days', value: '7' },
    { label: 'Last 30 days', value: '30' },
    { label: 'Last 90 days', value: '90' },
    { label: 'Custom range', value: 'custom' },
];

const comparisonOptions = [
    { label: 'No comparison', value: 'none' },
    { label: 'Previous period', value: 'previous_period' },
    { label: 'Previous year', value: 'previous_year' },
    { label: 'Previous year (match day of week)', value: 'previous_year_weekday' },
    { label: 'Custom period', value: 'custom' },
];

const comparisonReportKinds = new Set<ReportKind>([
    'daily_search_volume',
    'top_vehicle_searches',
]);

const toInputDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const initialToDate = toInputDate(new Date());
const initialFromDate = (() => {
    const date = new Date();
    date.setDate(date.getDate() - 29);
    return toInputDate(date);
})();

const shiftInputDate = (value: string, days: number) => {
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    date.setDate(date.getDate() + days);
    return toInputDate(date);
};

const parseInputDate = (value: string) => {
    const date = new Date(`${value.slice(0, 10)}T00:00:00`);
    return Number.isNaN(date.getTime()) ? new Date() : date;
};

const formatPickerDate = (date: Date) => date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
});

const formatComparisonRangeLabel = (from: string, to: string) => {
    const fromValue = parseInputDate(from);
    const toValue = parseInputDate(to);

    return `${fromValue.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    })} – ${toValue.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    })}`;
};

const getInputDateRangeDays = (from: string, to: string) => {
    const fromValue = new Date(`${from}T00:00:00`);
    const toValue = new Date(`${to}T00:00:00`);
    if (Number.isNaN(fromValue.getTime()) || Number.isNaN(toValue.getTime())) return 0;
    return Math.round((toValue.getTime() - fromValue.getTime()) / 86400000) + 1;
};

const getInitialDateRange = () => {
    const params = new URLSearchParams(window.location.search);
    const from = params.get('from') ?? '';
    const to = params.get('to') ?? '';
    const isCalendarDate = (value: string) => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
        const date = new Date(`${value}T00:00:00Z`);
        return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
    };
    const hasDateRange = isCalendarDate(from) && isCalendarDate(to) && from <= to;
    const fromDate = hasDateRange ? from : initialFromDate;
    const toDate = hasDateRange ? to : initialToDate;
    const comparisonTo = shiftInputDate(fromDate, -1);
    return {
        range: (hasDateRange ? 'custom' : '30') as ReportRange,
        from: fromDate,
        to: toDate,
        comparisonFrom: shiftInputDate(fromDate, -getInputDateRangeDays(fromDate, toDate)),
        comparisonTo,
    };
};

const formatCount = (value?: number) => (value ?? 0).toLocaleString();

type ReportSummaryMetricProps = {
    label: string;
    value: number | string;
    comparisonValue?: number;
    showComparison?: boolean;
    detail?: string;
};

const ReportSummaryMetric = ({
    label,
    value,
    comparisonValue,
    showComparison = false,
    detail,
}: ReportSummaryMetricProps) => {
    const numericValue = typeof value === 'number' ? value : undefined;
    const percentChange = showComparison
        && numericValue !== undefined
        && comparisonValue !== undefined
        ? comparisonValue === 0
            ? numericValue === 0 ? 0 : null
            : Math.round(((numericValue - comparisonValue) / comparisonValue) * 100)
        : undefined;
    const direction = percentChange == null || percentChange === 0
        ? ''
        : percentChange > 0 ? '↗' : '↘';
    const changeLabel = percentChange === null
        ? 'New'
        : percentChange === undefined
            ? undefined
            : `${Math.abs(percentChange)}%`;
    const accessibleChange = percentChange === null
        ? 'New compared with the selected comparison period'
        : percentChange === 0
            ? 'No change from the selected comparison period'
            : percentChange === undefined
                ? undefined
                : `${Math.abs(percentChange)} percent ${percentChange > 0 ? 'increase' : 'decrease'} from the selected comparison period`;

    return (
        <div className="pf-report-summary-metric">
            <span className="pf-report-summary-metric__label">{label}</span>
            <span className="pf-report-summary-metric__value-row">
                <strong>{typeof value === 'number' ? formatCount(value) : value}</strong>
            </span>
                {changeLabel ? (
                    <span
                        className="pf-report-summary-metric__change"
                        aria-label={accessibleChange}
                        title="Compared with the selected comparison period"
                    >
                        {direction} {changeLabel} <span className="pf-report-comparison-context">vs comparison period</span>
                    </span>
                ) : detail ? <span className="pf-report-summary-metric__change">{detail}</span> : null}
        </div>
    );
};

const formatDisplayDate = (value?: string) => {
    if (!value) return '-';

    const parsed = new Date(`${value.slice(0, 10)}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return '-';

    return parsed.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

const formatDateOnly = (value?: string) => value?.slice(0, 10) || '-';

const isSearchDataAvailableOn = (
    report: StorefrontSearchReport,
    date?: string
) => {
    if (!date) return false;
    // If an older API response does not include an availability boundary, do not
    // assume missing aggregate rows are real zero-search days.
    if (report.dataAvailableFrom === undefined) return false;

    const dataAvailableFrom = report.dataAvailableFrom?.slice(0, 10);
    return Boolean(dataAvailableFrom && date.slice(0, 10) >= dataAvailableFrom);
};

const isComparisonDataComplete = (report?: StorefrontSearchReport) => {
    if (!report?.comparisonEnabled) return true;
    if (report.dataAvailableFrom === undefined) return false;

    const dataAvailableFrom = report.dataAvailableFrom?.slice(0, 10);
    return Boolean(dataAvailableFrom && report.previousFrom.slice(0, 10) >= dataAvailableFrom);
};

const formatFieldName = (key: string) => key
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());

const escapeCsvCell = (value: string | number) => {
    let text = String(value ?? '');
    if (/^[=+\-@]/.test(text)) {
        text = `'${text}`;
    }

    return `"${text.replace(/"/g, '""')}"`;
};

const downloadDailyVolumeCsv = (report: StorefrontSearchReport) => {
    const headings = report.comparisonEnabled
        ? ['Date', 'Searches', 'Comparison date', 'Comparison searches']
        : ['Date', 'Searches'];
    const currentCounts = new Map(
        report.dailyCounts.map((day) => [formatDateOnly(day.date), day.searchCount])
    );
    const comparisonCounts = new Map(
        (report.previousDailyCounts ?? []).map((day) => [formatDateOnly(day.date), day.searchCount])
    );
    const periodDays = getInclusiveDayCount(report);
    const rows = Array.from({ length: periodDays }, (_, index) => {
        const currentDate = shiftInputDate(formatDateOnly(report.from), index);
        const comparisonDate = shiftInputDate(formatDateOnly(report.previousFrom), index);
        return [
            currentDate,
            isSearchDataAvailableOn(report, currentDate)
                ? currentCounts.get(currentDate) ?? 0
                : '',
            ...(report.comparisonEnabled
                ? [
                    comparisonDate,
                    isSearchDataAvailableOn(report, comparisonDate)
                        ? comparisonCounts.get(comparisonDate) ?? 0
                        : '',
                ]
                : []),
        ];
    });
    const csv = [headings, ...rows]
        .map((row) => row.map(escapeCsvCell).join(','))
        .join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `partfits-daily-search-volume-${formatDateOnly(report.from)}-to-${formatDateOnly(report.to)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
};

const downloadTopSearchesCsv = (report: StorefrontSearchReport, searches: StorefrontSearchReportTopSearch[]) => {
    const comparisonComplete = isComparisonDataComplete(report);
    const preferredKeys = ['year', 'make', 'model', 'submodel'];
    const discoveredKeys = Array.from(new Set(
        searches.flatMap((search) => Object.keys(search.fieldValues ?? {}))
    ));
    const fieldKeys = [
        ...preferredKeys.filter((key) => discoveredKeys.includes(key)),
        ...discoveredKeys.filter((key) => !preferredKeys.includes(key)).sort(),
    ];
    const headings = [
        'Vehicle',
        ...fieldKeys.map(formatFieldName),
        'Searches',
        'Search share (%)',
        ...(report.comparisonEnabled
            ? [
                comparisonComplete ? 'Comparison searches' : 'Comparison searches (partial)',
                'Change',
            ]
            : []),
        'Last searched',
    ];
    const rows = searches.map((search) => [
        getSearchLabel(search),
        ...fieldKeys.map((key) => search.fieldValues?.[key] ?? ''),
        search.searchCount,
        csvSharePercent(search.searchCount, report.totalSearches),
        ...(report.comparisonEnabled
            ? [
                search.previousSearchCount,
                comparisonComplete ? search.searchCount - search.previousSearchCount : '',
            ]
            : []),
        formatDateOnly(search.lastSearchedOn),
    ]);
    const csv = [headings, ...rows]
        .map((row) => row.map(escapeCsvCell).join(','))
        .join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `partfits-top-vehicle-searches-${formatDateOnly(report.from)}-to-${formatDateOnly(report.to)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
};

const downloadCoverageCsv = (
    report: StorefrontSearchReport,
    opportunities: StorefrontSearchReportTopSearch[],
    threshold: number
) => {
    const headings = ['Vehicle', 'Search demand', 'Search share (%)', 'Current mapped products', 'Last searched', 'Coverage status', 'Low coverage threshold (inclusive)'];
    const rows = opportunities.map((search) => [
        getSearchLabel(search),
        search.searchCount,
        csvSharePercent(search.searchCount, report.totalSearches),
        search.currentMappedProductCount,
        formatDateOnly(search.lastSearchedOn),
        search.currentMappedProductCount === 0 ? 'No mappings' : 'Low coverage',
        threshold,
    ]);
    const csv = [headings, ...rows]
        .map((row) => row.map(escapeCsvCell).join(','))
        .join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `partfits-coverage-opportunities-${formatDateOnly(report.from)}-to-${formatDateOnly(report.to)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
};

const downloadFitmentCoverageCsv = (
    report: FitmentCoverageHealthReport,
    vehicles: FitmentCoverageHealthVehicle[]
) => {
    const headings = ['Vehicle', 'Current mapped products', 'Coverage status', 'Low coverage threshold (inclusive)'];
    const rows = vehicles.map((vehicle) => [
        getSearchLabel(vehicle),
        vehicle.currentMappedProductCount,
        vehicle.currentMappedProductCount === 0 ? 'No mappings' : 'Low coverage',
        report.lowCoverageThreshold,
    ]);
    const csv = [headings, ...rows]
        .map((row) => row.map(escapeCsvCell).join(','))
        .join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `partfits-fitment-coverage-health-${formatDateOnly(report.generatedAt)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
};

const getPercentChange = (current: number, previous: number) => {
    if (previous === 0) return current === 0 ? 0 : null;
    return ((current - previous) / previous) * 100;
};

const formatPercentChange = (current: number, previous: number) => {
    const percent = getPercentChange(current, previous);
    if (percent == null) return 'New';
    const rounded = Math.round(percent);
    return `${rounded > 0 ? '+' : ''}${rounded.toLocaleString()}%`;
};

const getInclusiveDayCount = (report?: StorefrontSearchReport) => {
    if (!report?.from || !report.to) return 30;

    const from = new Date(`${report.from.slice(0, 10)}T00:00:00`);
    const to = new Date(`${report.to.slice(0, 10)}T00:00:00`);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return 30;

    return Math.max(1, Math.round((to.getTime() - from.getTime()) / 86400000) + 1);
};

const buildDailyBreakdownRows = (
    report: StorefrontSearchReport | undefined,
    days: number
): DailyBreakdownRow[] => {
    if (!report?.to || days <= 0) return [];

    const currentCounts = new Map(
        (report.dailyCounts ?? []).map((day) => [formatDateOnly(day.date), day.searchCount])
    );
    const comparisonCounts = new Map(
        (report.previousDailyCounts ?? []).map((day) => [formatDateOnly(day.date), day.searchCount])
    );
    const currentEnd = formatDateOnly(report.to);
    const comparisonEnd = formatDateOnly(report.previousTo);

    return Array.from({ length: days }, (_, index) => {
        const offset = -(days - index - 1);
        const currentDate = shiftInputDate(currentEnd, offset);
        const comparisonDate = report.comparisonEnabled
            ? shiftInputDate(comparisonEnd, offset)
            : undefined;

        return {
            currentDate,
            currentSearches: currentCounts.get(currentDate) ?? 0,
            currentDataAvailable: isSearchDataAvailableOn(report, currentDate),
            comparisonDate,
            comparisonSearches: comparisonDate
                ? comparisonCounts.get(comparisonDate) ?? 0
                : undefined,
            comparisonDataAvailable: comparisonDate
                ? isSearchDataAvailableOn(report, comparisonDate)
                : undefined,
        };
    });
};

const DailySearchVolume = ({ report, inProgressDate }: {
    report?: StorefrontSearchReport;
    inProgressDate?: string;
}) => {
    const [expanded, setExpanded] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const days = getInclusiveDayCount(report);
    const chartDays = Math.min(30, days);
    const rows = buildDailyBreakdownRows(report, days).reverse();
    const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
    const safePage = Math.min(page, pageCount);
    const start = (safePage - 1) * pageSize;
    const visibleRows = rows.slice(start, start + pageSize);
    const comparison = Boolean(report?.comparisonEnabled);
    const Chevron = expanded ? ChevronUpIcon : ChevronDownIcon;

    useEffect(() => setPage(1), [report, pageSize]);

    return <BlockStack gap="400">
        <div className="pf-report-chart">
            <div className="pf-report-chart-heading">
                <Text as="h2" variant="headingSm">Daily search volume</Text>
                <div className="pf-report-legend" aria-label="Chart legend">
                    <span><i className="pf-report-legend-current" aria-hidden="true" />Current period</span>
                    {comparison && <span><i className="pf-report-legend-previous" aria-hidden="true" />Comparison period</span>}
                </div>
            </div>
            {days > chartDays && <Text as="p" variant="bodySm" tone="subdued">Chart shows the most recent {chartDays} days. Daily breakdown and CSV include all {days} days.</Text>}
            <div className="pf-report-chart-unit">Searches</div>
            <SearchActivityChart report={report} days={chartDays} showComparison={comparison}
                variant="line" embedded inProgressDate={inProgressDate} />
        </div>
        <div className="pf-report-breakdown">
            <button type="button" className="pf-report-breakdown-toggle" aria-expanded={expanded}
                aria-controls="daily-report-details" onClick={() => setExpanded(open => !open)}>
                <span><strong>Daily breakdown</strong><span className="pf-report-breakdown-count">{rows.length} days</span></span>
                <span className="pf-report-breakdown-action">{expanded ? 'Hide details' : 'Show details'}<Chevron width={20} height={20} aria-hidden="true" /></span>
            </button>
            {expanded && <div id="daily-report-details">
                <BlockStack gap="300">
                    {comparison && !rows.every(day => day.comparisonDataAvailable) && <Banner tone="info">
                        Search tracking for this store begins on {formatDisplayDate(report?.dataAvailableFrom ?? undefined)}.
                        Earlier comparison dates are marked as no data and are not treated as zero-search days.
                    </Banner>}
                    <div className="pf-report-daily-table">
                        <table>
                            <thead><tr><th scope="col">Date</th><th scope="col">Searches</th>{comparison && <><th scope="col">Comparison</th><th scope="col">Change</th></>}</tr></thead>
                            <tbody>{visibleRows.map(day => {
                                const inProgress = day.currentDate === inProgressDate;
                                const comparable = day.currentDataAvailable && day.comparisonDataAvailable && !inProgress;
                                return <tr key={day.currentDate}>
                                    <td><time dateTime={day.currentDate}>{formatSearchDateLabel(day.currentDate)}<span className="pf-report-date-year">, {day.currentDate.slice(0, 4)}</span></time>{inProgress && <span className="pf-report-day-status">In progress</span>}</td>
                                    <td>{day.currentDataAvailable ? formatCount(day.currentSearches) : 'No data'}</td>
                                    {comparison && <>
                                        <td>{day.comparisonDataAvailable ? formatCount(day.comparisonSearches) : 'No data'}<time className="pf-report-comparison-date" dateTime={day.comparisonDate}>{formatSearchDateLabel(day.comparisonDate)}<span className="pf-report-date-year">, {day.comparisonDate?.slice(0, 4)}</span></time></td>
                                        <td>{inProgress ? <span aria-label="Day in progress; change not yet available">—</span> : comparable ? formatPercentChange(day.currentSearches, day.comparisonSearches ?? 0) : 'No data'}</td>
                                    </>}
                                </tr>;
                            })}</tbody>
                        </table>
                    </div>
                    {rows.length > 0 && <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                        <Text as="p" variant="bodySm" tone="subdued">{start + 1}–{Math.min(start + pageSize, rows.length)} of {rows.length} days</Text>
                        <InlineStack gap="300" blockAlign="center" wrap>
                            <Select label="Rows per page" labelInline value={String(pageSize)}
                                options={['10', '25', '50'].map(value => ({ label: value, value }))}
                                onChange={value => setPageSize(Number(value))} />
                            <Pagination accessibilityLabel="Daily search breakdown pagination"
                                accessibilityLabels={{ previous: 'Previous daily results page', next: 'Next daily results page' }}
                                hasPrevious={safePage > 1} hasNext={safePage < pageCount}
                                onPrevious={() => setPage(safePage - 1)} onNext={() => setPage(safePage + 1)}
                                label={`Page ${safePage} of ${pageCount}`} />
                        </InlineStack>
                    </InlineStack>}
                </BlockStack>
            </div>}
        </div>
    </BlockStack>;
};

const ReportsPage = () => {
    const [initialDates] = useState(getInitialDateRange);
    const [report, setReport] = useState<StorefrontSearchReport>();
    const [coverageHealth, setCoverageHealth] = useState<FitmentCoverageHealthReport>();
    const [selectedReport, setSelectedReport] = useState<ReportKind>(getInitialReport);
    const [vehicleSort, setVehicleSort] = useState<VehicleSort>(() =>
        new URLSearchParams(window.location.search).get('report') === 'search_demand_trends' ? 'increase' : 'searches');
    const [range, setRange] = useState<ReportRange>(initialDates.range);
    const [appliedRange, setAppliedRange] = useState<ReportRange>(initialDates.range);
    const [fromDate, setFromDate] = useState(initialDates.from);
    const [toDate, setToDate] = useState(initialDates.to);
    const [comparison, setComparison] = useState<ComparisonPeriod>('previous_period');
    const [comparisonFromDate, setComparisonFromDate] = useState(initialDates.comparisonFrom);
    const [comparisonToDate, setComparisonToDate] = useState(initialDates.comparisonTo);
    const [comparisonPickerOpen, setComparisonPickerOpen] = useState(false);
    const [comparisonPickerMonth, setComparisonPickerMonth] = useState(
        parseInputDate(initialDates.comparisonTo).getMonth()
    );
    const [comparisonPickerYear, setComparisonPickerYear] = useState(
        parseInputDate(initialDates.comparisonTo).getFullYear()
    );
    const [draftComparisonRange, setDraftComparisonRange] = useState<ComparisonDateRange>({
        start: parseInputDate(initialDates.comparisonFrom),
        end: parseInputDate(initialDates.comparisonTo),
    });
    const [aboutOpen, setAboutOpen] = useState(false);
    const [aboutInteracting, setAboutInteracting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [coverageLoading, setCoverageLoading] = useState(false);
    const [error, setError] = useState<string>();
    const [coverageError, setCoverageError] = useState<string>();
    const [coverageThreshold, setCoverageThreshold] = useState<number>();
    const [thresholdDraft, setThresholdDraft] = useState('5');
    const [coverageFilter, setCoverageFilter] = useState<CoverageFilter>('all');
    const [settingsError, setSettingsError] = useState<string>();
    const [settingsLoading, setSettingsLoading] = useState(true);
    const [settingsSaving, setSettingsSaving] = useState(false);
    const coverageRequestVersion = useRef(0);
    const isCoverageReport = selectedReport === 'coverage_opportunities' || selectedReport === 'fitment_coverage_health';
    const parsedThreshold = Number(thresholdDraft);
    const thresholdValid = /^\d+$/.test(thresholdDraft) && Number.isInteger(parsedThreshold)
        && parsedThreshold >= 1 && parsedThreshold <= 2147483647;
    const reportRequestVersion = useRef(0);

    const loadCoverageSettings = useCallback(async () => {
        setSettingsLoading(true);
        setSettingsError(undefined);
        try {
            const settings = await agent.Reports.coverageSettings();
            setCoverageThreshold(settings.lowCoverageThreshold);
            setThresholdDraft(String(settings.lowCoverageThreshold));
        } catch (requestError) {
            setSettingsError(await getApiErrorMessage(requestError, 'Unable to load coverage settings.'));
        } finally {
            setSettingsLoading(false);
        }
    }, []);

    useEffect(() => { void loadCoverageSettings(); }, [loadCoverageSettings]);

    const saveCoverageThreshold = async () => {
        if (!thresholdValid) return;
        setSettingsSaving(true);
        setSettingsError(undefined);
        try {
            const settings = await agent.Reports.updateCoverageSettings({ lowCoverageThreshold: parsedThreshold });
            ++coverageRequestVersion.current;
            setCoverageHealth(undefined);
            setCoverageError(undefined);
            setCoverageThreshold(settings.lowCoverageThreshold);
            setThresholdDraft(String(settings.lowCoverageThreshold));
        } catch (requestError) {
            setSettingsError(await getApiErrorMessage(requestError, 'Unable to save coverage threshold.'));
        } finally {
            setSettingsSaving(false);
        }
    };

    const loadReport = useCallback(async (
        selectedRange: ReportRange,
        selectedFrom: string,
        selectedTo: string,
        selectedComparison: ComparisonPeriod,
        selectedComparisonFrom: string,
        selectedComparisonTo: string
    ) => {
        const requestVersion = ++reportRequestVersion.current;
        if (selectedRange === 'custom' && (!selectedFrom || !selectedTo || selectedFrom > selectedTo)) {
            setLoading(false);
            setError('Choose a valid custom date range.');
            return;
        }

        if (selectedComparison === 'custom') {
            if (!selectedComparisonFrom || !selectedComparisonTo || selectedComparisonFrom > selectedComparisonTo) {
                setLoading(false);
                setError('Choose a valid custom comparison period.');
                return;
            }

            const selectedPeriodDays = selectedRange === 'custom'
                ? getInputDateRangeDays(selectedFrom, selectedTo)
                : Number(selectedRange);
            const comparisonPeriodDays = getInputDateRangeDays(
                selectedComparisonFrom,
                selectedComparisonTo
            );
            if (comparisonPeriodDays !== selectedPeriodDays) {
                setLoading(false);
                setError(`Choose a comparison period that is also ${selectedPeriodDays} days long.`);
                return;
            }
        }

        setLoading(true);
        setError(undefined);
        try {
            const response = await agent.Reports.searchActivity(selectedRange === 'custom'
                ? {
                    from: selectedFrom,
                    to: selectedTo,
                    comparison: selectedComparison,
                    comparisonFrom: selectedComparison === 'custom' ? selectedComparisonFrom : undefined,
                    comparisonTo: selectedComparison === 'custom' ? selectedComparisonTo : undefined,
                    limit: 250,
                }
                : {
                    days: Number(selectedRange),
                    comparison: selectedComparison,
                    comparisonFrom: selectedComparison === 'custom' ? selectedComparisonFrom : undefined,
                    comparisonTo: selectedComparison === 'custom' ? selectedComparisonTo : undefined,
                    limit: 250,
                });
            if (requestVersion !== reportRequestVersion.current) return;
            setReport(response);
            setAppliedRange(selectedRange);
        } catch (requestError) {
            const message = await getApiErrorMessage(requestError, 'Unable to load reports.');
            if (requestVersion === reportRequestVersion.current) setError(message);
        } finally {
            if (requestVersion === reportRequestVersion.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadReport(
            initialDates.range,
            initialDates.from,
            initialDates.to,
            'previous_period',
            initialDates.comparisonFrom,
            initialDates.comparisonTo
        );
    }, [initialDates, loadReport]);

    const loadCoverageHealth = useCallback(async () => {
        if (coverageThreshold === undefined) return;
        const version = ++coverageRequestVersion.current;
        setCoverageLoading(true);
        setCoverageError(undefined);
        try {
            const response = await agent.Reports.fitmentCoverage(250, coverageThreshold, coverageFilter);
            if (version === coverageRequestVersion.current) setCoverageHealth(response);
        } catch (requestError) {
            const message = await getApiErrorMessage(requestError, 'Unable to load fitment coverage health.');
            if (version === coverageRequestVersion.current) setCoverageError(message);
        } finally {
            if (version === coverageRequestVersion.current) setCoverageLoading(false);
        }
    }, [coverageThreshold, coverageFilter]);

    useEffect(() => {
        if (selectedReport === 'fitment_coverage_health') {
            setCoverageHealth(undefined);
            void loadCoverageHealth();
        }
        return () => { ++coverageRequestVersion.current; };
    }, [loadCoverageHealth, selectedReport]);

    const topSearches = report?.topSearches ?? [];
    const coverageOpportunities = [...topSearches]
        .filter((search) => coverageThreshold !== undefined && search.currentMappedProductCount <= coverageThreshold)
        .sort((left, right) => {
            const leftHasNoResults = left.currentMappedProductCount === 0;
            const rightHasNoResults = right.currentMappedProductCount === 0;

            if (leftHasNoResults !== rightHasNoResults) {
                return leftHasNoResults ? -1 : 1;
            }

            return right.searchCount - left.searchCount;
        });
    const noResultOpportunities = coverageOpportunities.filter((search) => search.currentMappedProductCount === 0);
    const lowCoverageOpportunities = coverageOpportunities.filter((search) => search.currentMappedProductCount > 0);
    const visibleOpportunities = coverageOpportunities.filter(search => coverageFilter === 'all'
        || (coverageFilter === 'unmapped' ? search.currentMappedProductCount === 0 : search.currentMappedProductCount > 0));
    const noResultSearchDemand = noResultOpportunities.reduce(
        (total, search) => total + search.searchCount,
        0
    );
    const comparisonDataComplete = isComparisonDataComplete(report);
    const canSortByChange = Boolean(report?.comparisonEnabled && comparisonDataComplete);
    const effectiveVehicleSort = canSortByChange ? vehicleSort : 'searches';
    const sortedSearches = [...topSearches]
        .sort((left, right) => {
            if (effectiveVehicleSort === 'searches') {
                return right.searchCount - left.searchCount;
            }

            const changeDifference = (right.searchCount - right.previousSearchCount)
                - (left.searchCount - left.previousSearchCount);
            return (effectiveVehicleSort === 'decrease' ? -changeDifference : changeDifference)
                || right.searchCount - left.searchCount;
        });
    const coverageHealthRows = coverageHealth?.vehicles ?? [];
    // Preset ranges end on the shop's current day, as determined by the API.
    const inProgressDate = appliedRange !== 'custom' ? report?.to.slice(0, 10) : undefined;
    const supportsComparison = comparisonReportKinds.has(selectedReport);
    const detailKey = JSON.stringify([selectedReport, report?.generatedAt, report?.from, report?.to,
        report?.comparison, report?.previousFrom, report?.previousTo, coverageHealth?.generatedAt, coverageThreshold, coverageFilter, effectiveVehicleSort]);
    const comparisonNotice = report?.comparisonEnabled && !comparisonDataComplete ? (
        <Banner tone="info">
            Comparison totals are partial because this period begins before search tracking started
            on {formatDisplayDate(report.dataAvailableFrom ?? undefined)}. Changes are withheld.
        </Banner>
    ) : undefined;
    const fitmentCoverageRate = coverageHealth?.totalVehicles
        ? (coverageHealth.vehiclesWithMappings / coverageHealth.totalVehicles) * 100
        : 0;

    const getDefaultCustomComparisonRange = (): ComparisonDateRange => {
        const selectedPeriodDays = range === 'custom'
            ? getInputDateRangeDays(fromDate, toDate)
            : Number(range);
        const currentPeriodStart = range === 'custom'
            ? fromDate
            : shiftInputDate(formatDateOnly(report?.to ?? initialToDate), -(selectedPeriodDays - 1));
        const defaultComparisonTo = shiftInputDate(currentPeriodStart, -1);
        const defaultComparisonFrom = shiftInputDate(
            defaultComparisonTo,
            -(selectedPeriodDays - 1)
        );

        return {
            start: parseInputDate(defaultComparisonFrom),
            end: parseInputDate(defaultComparisonTo),
        };
    };

    const openComparisonPicker = (rangeToEdit?: ComparisonDateRange) => {
        const nextRange = rangeToEdit ?? {
            start: parseInputDate(comparisonFromDate),
            end: parseInputDate(comparisonToDate),
        };
        setDraftComparisonRange(nextRange);
        setComparisonPickerMonth(nextRange.end.getMonth());
        setComparisonPickerYear(nextRange.end.getFullYear());
        setComparisonPickerOpen(true);
    };

    const closeComparisonPicker = () => {
        setDraftComparisonRange({
            start: parseInputDate(comparisonFromDate),
            end: parseInputDate(comparisonToDate),
        });
        setComparisonPickerOpen(false);
    };

    const handleComparisonChange = (value: string) => {
        const nextComparison = value as ComparisonPeriod;
        setComparison(nextComparison);

        if (nextComparison !== 'custom') {
            setComparisonPickerOpen(false);
            if (range !== 'custom') {
                void loadReport(range, fromDate, toDate, nextComparison, comparisonFromDate, comparisonToDate);
            }
            return;
        }

        const defaultRange = getDefaultCustomComparisonRange();
        const defaultFrom = toInputDate(defaultRange.start);
        const defaultTo = toInputDate(defaultRange.end);
        setComparisonFromDate(defaultFrom);
        setComparisonToDate(defaultTo);
        openComparisonPicker(defaultRange);
    };

    const expectedComparisonDays = range === 'custom'
        ? getInputDateRangeDays(fromDate, toDate)
        : Number(range);
    const draftComparisonDays = getInputDateRangeDays(
        toInputDate(draftComparisonRange.start),
        toInputDate(draftComparisonRange.end)
    );
    const customComparisonIsValid = expectedComparisonDays > 0
        && draftComparisonDays === expectedComparisonDays;
    const currentPeriodStart = range === 'custom'
        ? fromDate
        : shiftInputDate(
            formatDateOnly(report?.to ?? initialToDate),
            -(Math.max(1, expectedComparisonDays) - 1)
        );
    const latestComparisonDate = parseInputDate(shiftInputDate(currentPeriodStart, -1));

    const applyComparisonPicker = () => {
        if (!customComparisonIsValid) return;
        setComparisonFromDate(toInputDate(draftComparisonRange.start));
        setComparisonToDate(toInputDate(draftComparisonRange.end));
        setComparisonPickerOpen(false);
        void loadReport(range, fromDate, toDate, 'custom', toInputDate(draftComparisonRange.start), toInputDate(draftComparisonRange.end));
    };

    const returnToComparisonOptions = () => {
        closeComparisonPicker();
        setComparison('previous_period');
        if (range !== 'custom') {
            void loadReport(range, fromDate, toDate, 'previous_period', comparisonFromDate, comparisonToDate);
        }
    };

    const selectedReportLabel = reportDefinitions.find(
        (definition) => definition.value === selectedReport
    )?.label ?? 'Report';
    const aboutReport = (
        <Popover
            active={aboutOpen || aboutInteracting}
            activator={(
                <span
                    className="pf-report-about-trigger"
                    onMouseEnter={() => setAboutInteracting(true)}
                    onMouseLeave={() => setAboutInteracting(false)}
                >
                    <Button
                        icon={InfoIcon}
                        onClick={() => setAboutOpen((open) => !open)}
                        onFocus={() => setAboutInteracting(true)}
                        onBlur={() => setAboutInteracting(false)}
                        accessibilityLabel="About this report"
                        ariaExpanded={aboutOpen || aboutInteracting}
                    />
                </span>
            )}
            onClose={() => {
                setAboutOpen(false);
                setAboutInteracting(false);
            }}
            preferredPosition="below"
            preferredAlignment="right"
            sectioned
        >
            <div className="pf-report-about-popover">
                <BlockStack gap="150">
                    <Text as="h2" variant="headingSm">
                        {selectedReportLabel}
                    </Text>
                    <Text as="p" variant="bodySm">
                        Data is aggregated at the store level and does not include customer, session, or device information.
                        Search trends compare equal-length periods. Coverage reports use current product mappings.
                    </Text>
                </BlockStack>
            </div>
        </Popover>
    );

    const exportDisabled = selectedReport === 'fitment_coverage_health'
        ? coverageHealthRows.length === 0
        : selectedReport === 'coverage_opportunities'
            ? visibleOpportunities.length === 0
            : selectedReport === 'daily_search_volume'
                ? !report?.dailyCounts.length
                : !report?.topSearches.length;
    const exportReport = () => {
        if (selectedReport === 'coverage_opportunities') {
            if (!report) return;
            if (coverageThreshold === undefined) return;
            downloadCoverageCsv(report, visibleOpportunities, coverageThreshold);
            return;
        }

        if (selectedReport === 'fitment_coverage_health') {
            if (!coverageHealth) return;
            downloadFitmentCoverageCsv(coverageHealth, coverageHealthRows);
            return;
        }

        if (!report) return;
        if (selectedReport === 'daily_search_volume') {
            downloadDailyVolumeCsv(report);
            return;
        }

        downloadTopSearchesCsv(report, sortedSearches);
    };

    return (
        <Page
            title={FEATURES.reports}
            primaryAction={(
                <InlineStack gap="200" blockAlign="center">
                    <Button
                        icon={ExportIcon}
                        onClick={exportReport}
                        disabled={exportDisabled || (isCoverageReport && (settingsLoading || settingsSaving || coverageThreshold === undefined)) || (selectedReport === 'fitment_coverage_health'
                            ? coverageLoading || Boolean(coverageError)
                            : loading || Boolean(error))}
                    >
                        Export CSV
                    </Button>
                    {aboutReport}
                </InlineStack>
            )}
        >
            <div className="pf-reports">
                <BlockStack gap="400">
                    {selectedReport !== 'fitment_coverage_health' && error && (
                        <Banner tone="critical" action={{ content: 'Retry', onAction: () => {
                            void loadReport(range, fromDate, toDate, comparison, comparisonFromDate, comparisonToDate);
                        } }}>{error}</Banner>
                    )}
                    {selectedReport === 'fitment_coverage_health' && coverageError && (
                        <Banner tone="critical">{coverageError}</Banner>
                    )}

                    <Card padding="0">
                    <div className="pf-report-controls-section">
                        <BlockStack gap="300">
                            <div className="pf-report-controls">
                                <div className="pf-report-control">
                                    <Select
                                        label="Report"
                                        options={reportOptions}
                                        value={selectedReport}
                                        onChange={(value) => setSelectedReport(value as ReportKind)}
                                    />
                                </div>
                                {selectedReport === 'fitment_coverage_health' ? (
                                    <>
                                        <div className="pf-report-control">
                                            <Text as="p" variant="bodySm" tone="subdued">
                                                Current mapping snapshot
                                            </Text>
                                        </div>
                                        <Button
                                            onClick={loadCoverageHealth}
                                            loading={coverageLoading}
                                            disabled={coverageThreshold === undefined || settingsSaving}
                                        >
                                            Refresh
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <div className="pf-report-control">
                                            <Select
                                                label="Date range"
                                                options={rangeOptions}
                                                value={range}
                                                onChange={(value) => {
                                                    const nextRange = value as ReportRange;
                                                    setRange(nextRange);
                                                    if (nextRange !== 'custom' && (!supportsComparison || comparison !== 'custom')) {
                                                        void loadReport(nextRange, fromDate, toDate, supportsComparison ? comparison : 'previous_period', comparisonFromDate, comparisonToDate);
                                                    }
                                                }}
                                            />
                                        </div>
                                        {range === 'custom' && (
                                            <>
                                                <div className="pf-report-control">
                                                    <TextField
                                                        label="From"
                                                        type="date"
                                                        autoComplete="off"
                                                        value={fromDate}
                                                        max={toDate}
                                                        onChange={setFromDate}
                                                    />
                                                </div>
                                                <div className="pf-report-control">
                                                    <TextField
                                                        label="To"
                                                        type="date"
                                                        autoComplete="off"
                                                        value={toDate}
                                                        min={fromDate}
                                                        max={initialToDate}
                                                        onChange={setToDate}
                                                    />
                                                </div>
                                            </>
                                        )}
                                        {supportsComparison && comparison !== 'custom' && (
                                            <div className="pf-report-control">
                                                <Select
                                                    label="Compare to"
                                                    options={comparisonOptions}
                                                    value={comparison}
                                                    onChange={handleComparisonChange}
                                                />
                                            </div>
                                        )}
                                        {supportsComparison && comparison === 'custom' && (
                                            <div className="pf-report-control">
                                                <BlockStack gap="100">
                                                    <Text as="span" variant="bodySm">
                                                        Compare to
                                                    </Text>
                                                    <Popover
                                                        active={comparisonPickerOpen}
                                                        activator={(
                                                            <Button
                                                                icon={CalendarIcon}
                                                                disclosure={comparisonPickerOpen ? 'up' : 'down'}
                                                                onClick={() => openComparisonPicker()}
                                                                fullWidth
                                                                textAlign="left"
                                                                accessibilityLabel={`Custom comparison period: ${formatComparisonRangeLabel(comparisonFromDate, comparisonToDate)}`}
                                                            >
                                                                {formatComparisonRangeLabel(
                                                                    comparisonFromDate,
                                                                    comparisonToDate
                                                                )}
                                                            </Button>
                                                        )}
                                                        onClose={closeComparisonPicker}
                                                        preferredPosition="below"
                                                        preferredAlignment="left"
                                                        fluidContent
                                                        autofocusTarget="none"
                                                    >
                                                        <div className="pf-comparison-date-picker">
                                                            <div className="pf-comparison-date-picker__header">
                                                                <Button
                                                                    variant="plain"
                                                                    icon={ArrowLeftIcon}
                                                                    onClick={returnToComparisonOptions}
                                                                >
                                                                    Back
                                                                </Button>
                                                            </div>

                                                            <Divider />

                                                            <div className="pf-comparison-date-picker__body">
                                                                <div className="pf-comparison-date-picker__range">
                                                                    <div>
                                                                        <Text as="span" variant="bodySm" tone="subdued">
                                                                            From
                                                                        </Text>
                                                                        <Text as="p" fontWeight="medium">
                                                                            {formatPickerDate(draftComparisonRange.start)}
                                                                        </Text>
                                                                    </div>
                                                                    <span aria-hidden="true">→</span>
                                                                    <div>
                                                                        <Text as="span" variant="bodySm" tone="subdued">
                                                                            To
                                                                        </Text>
                                                                        <Text as="p" fontWeight="medium">
                                                                            {formatPickerDate(draftComparisonRange.end)}
                                                                        </Text>
                                                                    </div>
                                                                </div>

                                                                <DatePicker
                                                                    month={comparisonPickerMonth}
                                                                    year={comparisonPickerYear}
                                                                    selected={draftComparisonRange}
                                                                    allowRange
                                                                    disableDatesAfter={latestComparisonDate}
                                                                    onChange={setDraftComparisonRange}
                                                                    onMonthChange={(month, year) => {
                                                                        setComparisonPickerMonth(month);
                                                                        setComparisonPickerYear(year);
                                                                    }}
                                                                />

                                                                <Text
                                                                    as="p"
                                                                    variant="bodySm"
                                                                    tone={customComparisonIsValid ? 'subdued' : 'critical'}
                                                                >
                                                                    {customComparisonIsValid
                                                                        ? `${draftComparisonDays} days selected, matching the report period.`
                                                                        : `Select exactly ${expectedComparisonDays} days to match the report period. ${draftComparisonDays} currently selected.`}
                                                                </Text>
                                                            </div>

                                                            <Divider />

                                                            <div className="pf-comparison-date-picker__footer">
                                                                <Button onClick={closeComparisonPicker}>Cancel</Button>
                                                                <Button
                                                                    variant="primary"
                                                                    disabled={!customComparisonIsValid}
                                                                    onClick={applyComparisonPicker}
                                                                >
                                                                    Apply
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </Popover>
                                                </BlockStack>
                                            </div>
                                        )}
                                        {(range === 'custom' || (supportsComparison && comparison === 'custom')) && <Button
                                            variant="primary"
                                            onClick={() => loadReport(
                                                range,
                                                fromDate,
                                                toDate,
                                                supportsComparison ? comparison : 'previous_period',
                                                comparisonFromDate,
                                                comparisonToDate
                                            )}
                                            loading={loading}
                                        >
                                            Apply
                                        </Button>}
                                    </>
                                )}
                            </div>
                            {isCoverageReport && <BlockStack gap="300">
                                {settingsError && <Banner tone="critical"
                                    action={coverageThreshold === undefined ? { content: 'Retry', onAction: () => { void loadCoverageSettings(); } } : undefined}>
                                    {settingsError}
                                </Banner>}
                                <div className="pf-report-coverage-controls">
                                    <TextField label="Flag low coverage at" type="number" autoComplete="off"
                                        value={thresholdDraft} onChange={setThresholdDraft} min={1} max={2147483647} step={1}
                                        suffix="mapped products or fewer"
                                        disabled={settingsLoading || settingsSaving || coverageThreshold === undefined}
                                        error={!thresholdValid ? 'Enter a positive whole number (up to 2,147,483,647).' : undefined} />
                                    <Button onClick={saveCoverageThreshold} loading={settingsSaving}
                                        disabled={settingsLoading || coverageThreshold === undefined || !thresholdValid || parsedThreshold === coverageThreshold}>
                                        Save threshold
                                    </Button>
                                    <Select label="Show" value={coverageFilter}
                                        options={[{ label: 'All needing attention', value: 'all' },
                                            { label: 'No mappings', value: 'unmapped' }, { label: 'Low coverage', value: 'low' }]}
                                        onChange={value => setCoverageFilter(value as CoverageFilter)} />
                                </div>
                                <Text as="p" variant="bodySm" tone="subdued">
                                    {settingsLoading ? 'Loading saved threshold…' : `Saved for this store and shared by both coverage reports. Zero mapped products are always “No mappings”.${coverageThreshold === undefined ? '' : ` Current threshold: ${coverageThreshold} or fewer.`}`}
                                    {' '}Show filters the list and CSV; summary metrics include both groups.
                                </Text>
                            </BlockStack>}
                            <div className="pf-report-period" aria-live="polite">
                                {selectedReport === 'fitment_coverage_health'
                                    ? coverageHealth && `Updated ${new Date(coverageHealth.generatedAt).toLocaleString()}`
                                    : report && <>
                                        {formatComparisonRangeLabel(report.from, report.to)}
                                        {supportsComparison && report.comparisonEnabled && <> · Compared with {formatComparisonRangeLabel(report.previousFrom, report.previousTo)}</>}
                                    </>}
                                {loading && selectedReport !== 'fitment_coverage_health' && <Spinner size="small" accessibilityLabel="Updating report" />}
                            </div>
                        </BlockStack>
                    </div>

                    {(isCoverageReport && settingsLoading) || (selectedReport === 'fitment_coverage_health'
                        ? coverageLoading && !coverageHealth
                        : loading && !report) ? (
                        <div className="pf-report-section">
                            <InlineStack align="center">
                                <Spinner accessibilityLabel="Loading reports" />
                            </InlineStack>
                        </div>
                    ) : (isCoverageReport && coverageThreshold === undefined) || (selectedReport === 'fitment_coverage_health' && !coverageHealth) ? null : (
                        <>
                        {selectedReport === 'daily_search_volume' || selectedReport === 'top_vehicle_searches' ? (
                            <div className="pf-report-section">
                                <BlockStack gap="400">
                                <div className="pf-report-summary-metrics">
                                    <ReportSummaryMetric
                                        label="Total searches"
                                        value={report?.totalSearches ?? 0}
                                        comparisonValue={report?.previousTotalSearches ?? 0}
                                        showComparison={Boolean(
                                            report?.comparisonEnabled && comparisonDataComplete
                                        )}
                                    />
                                    <ReportSummaryMetric
                                        label="Unique vehicles searched"
                                        value={report?.uniqueSearchCount ?? 0}
                                        comparisonValue={report?.previousUniqueSearchCount ?? 0}
                                        showComparison={Boolean(
                                            report?.comparisonEnabled && comparisonDataComplete
                                        )}
                                    />
                                    <ReportSummaryMetric
                                        label="Searches today"
                                        value={report?.searchesToday ?? 0}
                                        detail="Day in progress"
                                    />
                                </div>

                                <Divider />

                                {selectedReport === 'daily_search_volume' ? (
                                    <DailySearchVolume report={report} inProgressDate={inProgressDate} />
                                ) : (
                                    <VehicleReportDetails key={detailKey} mode="searches" title="Top vehicle searches"
                                        rows={sortedSearches} totalSearches={report?.totalSearches} comparison={report?.comparisonEnabled}
                                        comparisonComplete={comparisonDataComplete} notice={comparisonNotice}
                                        controls={<div className="pf-report-detail-controls">
                                            <Select label="Sort by" labelInline value={effectiveVehicleSort}
                                                options={[
                                                    { label: 'Most searched', value: 'searches' },
                                                    { label: 'Biggest increases', value: 'increase', disabled: !canSortByChange },
                                                    { label: 'Biggest decreases', value: 'decrease', disabled: !canSortByChange },
                                                ]} onChange={value => setVehicleSort(value as VehicleSort)} />
                                            {!canSortByChange && <Text as="p" variant="bodySm" tone="subdued">Change sorting needs a complete comparison period.</Text>}
                                        </div>}
                                        description="Includes up to 250 of the most searched vehicle combinations. Change sorting ranks these vehicles by the difference in search counts. New means no searches in the comparison period."
                                        emptyMessage="No vehicle searches were recorded in this range." />
                                )}
                                </BlockStack>
                            </div>
                        ) : selectedReport === 'coverage_opportunities' ? (
                            <div className="pf-report-section">
                                <BlockStack gap="400">
                                    <div className="pf-report-summary-metrics">
                                        <ReportSummaryMetric label="Vehicles with no mappings" value={noResultOpportunities.length} />
                                        <ReportSummaryMetric label="Searches with no mappings" value={noResultSearchDemand}
                                            detail={report && report.totalSearches > 0 ? `${formatSharePercent(getSharePercent(noResultSearchDemand, report.totalSearches))} of all recorded searches` : undefined} />
                                        <ReportSummaryMetric label="Low-coverage vehicles" value={lowCoverageOpportunities.length}
                                            detail={`1–${coverageThreshold} mapped products`} />
                                    </div>
                                    <Divider />
                                    <VehicleReportDetails key={detailKey} mode="opportunities" title="Coverage opportunities"
                                        rows={visibleOpportunities} totalSearches={report?.totalSearches}
                                        description={`No mappings first, then 1–${coverageThreshold} mapped products, ranked by searches. Metrics and results use current mappings for up to 250 of the most searched vehicles. List and CSV respect the Show filter.`}
                                        emptyMessage={topSearches.length === 0
                                            ? 'No vehicle searches were recorded in this range.'
                                            : 'No searched vehicles match the saved threshold and Show filter.'} />
                                </BlockStack>
                            </div>
                        ) : (
                            <div className="pf-report-section">
                                <BlockStack gap="400">
                                    <div className="pf-report-summary-metrics pf-report-summary-metrics--four">
                                        <ReportSummaryMetric label="Active vehicles" value={coverageHealth?.totalVehicles ?? 0}
                                            detail={coverageHealth ? `${coverageHealth.averageMappedProducts.toFixed(1)} products per covered vehicle` : undefined} />
                                        <ReportSummaryMetric label="Vehicles without mappings" value={coverageHealth?.vehiclesWithoutMappings ?? 0}
                                            detail={coverageHealth && coverageHealth.totalVehicles > 0 ? `${formatSharePercent(getSharePercent(coverageHealth.vehiclesWithoutMappings, coverageHealth.totalVehicles))} of active vehicles` : undefined} />
                                        <ReportSummaryMetric label="Low-coverage vehicles" value={coverageHealth?.lowCoverageVehicles ?? 0}
                                            detail={`${coverageHealth && coverageHealth.totalVehicles > 0 ? `${formatSharePercent(getSharePercent(coverageHealth.lowCoverageVehicles, coverageHealth.totalVehicles))} of active vehicles · ` : ''}1–${coverageThreshold} mapped products`} />
                                        <ReportSummaryMetric label="Coverage rate" value={`${fitmentCoverageRate.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`}
                                            detail="At least one mapped product" />
                                    </div>
                                    <Divider />
                                    <VehicleReportDetails key={detailKey} mode="health" title="Vehicles needing attention"
                                        rows={coverageHealthRows}
                                        description={`Up to 250 vehicles with no mappings or 1–${coverageThreshold} mapped products, using the Show filter. Unmapped vehicles appear first. Summary metrics cover the full active catalog.`}
                                        emptyMessage={(coverageHealth?.totalVehicles ?? 0) === 0
                                            ? 'No active vehicles are available in the current fitment catalog.'
                                            : 'No active vehicles match the saved threshold and Show filter.'} />
                                </BlockStack>
                            </div>
                        )}
                        </>
                    )}
                    </Card>
                </BlockStack>
            </div>
        </Page>
    );
};

export default ReportsPage;
